/**
 * <purchase-bar> — reveals the sticky bottom purchase bar by scroll direction,
 * but only after the main buy box has scrolled out of view (so it never
 * duplicates an on-screen CTA). On desktop the buy box is sticky and stays
 * visible through the product section, so the bar effectively only appears on
 * mobile, where the buy box scrolls away and the bar takes over.
 *
 * Hidden by default — pure enhancement. Without JS the bar stays off-screen and
 * the in-page buy box is the canonical, fully-working purchase point.
 * Passive listener, rAF-throttled, IntersectionObserver-gated.
 */
if (!customElements.get('purchase-bar')) {
  customElements.define(
    'purchase-bar',
    class PurchaseBar extends HTMLElement {
      connectedCallback() {
        this.lastY = window.scrollY;
        this.ticking = false;
        this.armed = false; // true once the buy box is out of view

        const target = document.querySelector(this.getAttribute('data-target') || '');
        if (target && 'IntersectionObserver' in window) {
          this.io = new IntersectionObserver(
            (entries) => {
              // Out of view ⇒ eligible; back in view ⇒ disarm and hide.
              this.armed = !entries[0].isIntersecting;
              if (!this.armed) this.hide();
            },
            // Shrink the root's top by ~the sticky header so the buy box counts
            // as gone once it slips behind the header, not after it fully clears.
            { rootMargin: '-100px 0px 0px 0px' }
          );
          this.io.observe(target);
        }
        // If there's no target to observe, stay safe (never reveal) rather than
        // arming everywhere and showing the bar over an on-screen buy box.

        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            // Clamp iOS rubber-band overscroll so the bounce-back isn't read as
            // a direction change.
            const maxY = document.documentElement.scrollHeight - window.innerHeight;
            const y = Math.min(Math.max(0, window.scrollY), maxY);
            const delta = y - this.lastY;
            // Near the very bottom, hide so the bar never covers the footer.
            const nearBottom = maxY - y < this.offsetHeight;
            if (Math.abs(delta) > 4) {
              // hysteresis vs trackpad jitter
              if (this.armed && delta < 0 && !nearBottom) {
                this.show(); // armed + scroll up + not at the bottom → reveal
              } else {
                this.hide(); // scroll down, not armed, or at the bottom → hide
              }
              this.lastY = y;
            }
            this.ticking = false;
          });
        };

        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      disconnectedCallback() {
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
        if (this.io) this.io.disconnect();
      }

      show() {
        this.classList.add('is-visible');
      }

      hide() {
        // Never pull the bar (and its focusable CTAs) out from under a keyboard
        // or screen-reader user — same guard as sticky-header.js.
        if (this.contains(document.activeElement)) return;
        this.classList.remove('is-visible');
      }
    }
  );
}
